import { type DispatchCtx, dispatchMessage } from './dispatchMessage.js';
import type { ChatMessage, LobbyView, MatchInfo, MpPhase } from './types';

type Listener = (msg: Record<string, unknown>) => void;

export interface ReconnectOptions {
  maxAttempts: number;
  baseDelays: readonly number[];
  connectionTimeoutMs: number;
}

const DEFAULT_RECONNECT_OPTIONS: ReconnectOptions = {
  maxAttempts: 6,
  baseDelays: [1000, 2000, 4000, 8000, 16000, 30000],
  connectionTimeoutMs: 5000,
};

export class MpClient {
  private gw: WebSocket | null = null;
  private ms: WebSocket | null = null;
  private phase: MpPhase = 'idle';
  private listeners = new Map<string, Set<Listener>>();
  private lobbyView: LobbyView | null = null;
  private matchInfo: MatchInfo | null = null;
  private chatHistory: ChatMessage[] = [];
  private currentPlayerId?: string;
  private selfId?: string; // our lobby accountId (lobby:created / lobby:joined)
  /** Better Auth user id for the signed-in player — the identity backend
   *  services see. Set at queue time so match:found can derive localPlayerId
   *  for queued players who never joined a lobby. */
  private authIdentity?: string;
  private reconnectToken?: string;
  private reconnectAttempts = 0;
  private serverUrl?: string;
  private gatewayUrl?: string;
  private gatewayAuthed = false;
  private reconnectOptions: ReconnectOptions = DEFAULT_RECONNECT_OPTIONS;

  /** Connect to the gateway. `token` (a signed @aod/auth service token) is
   *  appended as ?token= so the gateway can bind the socket to the authed
   *  sub — required for ranked queues and match-found pushes. */
  async connectGateway(url: string, token?: string): Promise<void> {
    const wsUrl = token
      ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
      : url;
    this.gatewayUrl = url;
    this.gatewayAuthed = token !== undefined;
    return new Promise((resolve, reject) => {
      this.gw = new WebSocket(wsUrl);
      this.gw.addEventListener('open', () => {
        this.phase = 'lobby';
        resolve();
      });
      this.gw.addEventListener('error', () => reject(new Error('gateway connection failed')));
      this.gw.addEventListener('message', (e) => this.dispatch(JSON.parse(e.data as string)));
      this.gw.addEventListener('close', () => this.handleClose('gateway'));
    });
  }

  /** True when the current gateway socket was opened with an auth token. */
  isGatewayAuthed(): boolean {
    return this.gatewayAuthed;
  }

  /** Reconnect the gateway socket with a service token if it isn't authed
   *  yet. No-op when already authed. Resolves false when the reconnect
   *  fails (the anonymous socket is left alone). */
  async ensureAuthedGateway(token: string): Promise<boolean> {
    if (this.gatewayAuthed) return true;
    if (!this.gatewayUrl) return false;
    this.gw?.close();
    this.gw = null;
    try {
      await this.connectGateway(this.gatewayUrl, token);
      return true;
    } catch {
      return false;
    }
  }

  async connectMatchServer(url: string): Promise<void> {
    this.serverUrl = url;
    return new Promise((resolve, reject) => {
      this.ms = new WebSocket(url);
      this.ms.addEventListener('open', () => {
        this.phase = 'match';
        resolve();
      });
      this.ms.addEventListener('error', () => reject(new Error('match-server connection failed')));
      this.ms.addEventListener('message', (e) => this.dispatch(JSON.parse(e.data as string)));
      this.ms.addEventListener('close', () => this.handleClose('match-server'));
    });
  }

  // ── Lobby actions ──

  createLobby(displayName: string): void {
    this.send('gw', { type: 'lobby:create', displayName });
  }

  joinLobby(inviteCode: string, displayName: string): void {
    this.send('gw', { type: 'lobby:join', inviteCode, displayName });
  }

  leaveLobby(): void {
    this.send('gw', { type: 'lobby:leave' });
  }

  startMatch(): void {
    this.send('gw', { type: 'lobby:start' });
  }

  updateConfig(
    maxPlayers: number,
    mapSize: 'small' | 'medium' | 'large',
    format?: string,
    presetId?: string,
    teams?: boolean,
  ): void {
    this.send('gw', {
      type: 'lobby:config',
      maxPlayers,
      mapSize,
      format,
      ...(presetId ? { presetId } : {}),
      ...(teams !== undefined ? { teams } : {}),
    });
  }

  addAiSlot(
    displayName: string,
    difficulty: 'easy' | 'normal' | 'hard',
    aiFogOfWar?: boolean,
  ): void {
    this.send('gw', { type: 'lobby:add-ai', displayName, difficulty, aiFogOfWar });
  }

  removeAiSlot(slotId: string): void {
    this.send('gw', { type: 'lobby:remove-ai', slotId });
  }

  setReady(): void {
    this.send('gw', { type: 'lobby:ready' });
  }

  setUnready(): void {
    this.send('gw', { type: 'lobby:unready' });
  }

  // ── Queue actions ──

  joinQueue(format: string, ranked: boolean, displayName: string, region?: string): void {
    this.send('gw', { type: 'queue', format, ranked, displayName, region });
  }

  leaveQueue(): void {
    this.send('gw', { type: 'queue:leave' });
  }

  // ── Match actions ──

  sendIntents(tick: number, actions: readonly unknown[]): void {
    this.send('ms', { type: 'intents', tick, actions });
  }

  // ── Intents ──

  // ── Chat ──

  sendChat(sender: string, text: string): void {
    const channel = this.phase === 'lobby' ? 'gw' : 'ms';
    this.send(channel, { type: 'chat:say', sender, text });
    this.chatHistory.push({ sender, text, timestamp: Date.now() });
    this.emit('chat', { sender, text });
  }

  /** Join the match after connecting to match-server. */
  joinMatch(playerId: string, displayName: string, matchId?: string): void {
    this.currentPlayerId = playerId;
    this.send('ms', { type: 'player:join', playerId, displayName, ...(matchId && { matchId }) });
  }

  getChatHistory(): readonly ChatMessage[] {
    return this.chatHistory;
  }

  // ── Event subscription ──

  on(event: string, listener: Listener): () => void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)?.add(listener);
    return () => this.listeners.get(event)?.delete(listener);
  }

  // ── State accessors ──

  getPhase(): MpPhase {
    return this.phase;
  }

  getLobbyView(): LobbyView | null {
    return this.lobbyView;
  }

  getMatchInfo(): MatchInfo | null {
    return this.matchInfo;
  }

  getCurrentPlayerId(): string | undefined {
    return this.currentPlayerId;
  }

  setAuthIdentity(sub: string): void {
    this.authIdentity = sub;
  }

  getAuthIdentity(): string | undefined {
    return this.authIdentity;
  }

  sendMessage(payload: Record<string, unknown>): void {
    const channel = this.phase === 'lobby' ? 'gw' : 'ms';
    this.send(channel, payload);
  }

  // ── Internal ──

  private send(channel: 'gw' | 'ms', payload: Record<string, unknown>): void {
    const ws = channel === 'gw' ? this.gw : this.ms;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(payload));
  }

  private dispatch(msg: Record<string, unknown>): void {
    // Each player gets their own reconnect credential at match start.
    if (msg.type === 'match:starting' && typeof msg.reconnectToken === 'string') {
      this.reconnectToken = msg.reconnectToken;
    }
    const self = this;
    const ctx: DispatchCtx = {
      get lobbyView() {
        return self.lobbyView;
      },
      setLobbyView: (v) => {
        self.lobbyView = v;
      },
      get matchInfo() {
        return self.matchInfo;
      },
      setMatchInfo: (v) => {
        self.matchInfo = v;
      },
      get chatHistory() {
        return self.chatHistory;
      },
      pushChat: (m) => {
        self.chatHistory.push(m);
      },
      get selfId() {
        return self.selfId;
      },
      set selfId(v: string | undefined) {
        self.selfId = v;
      },
      closeGw: () => {
        self.gw?.close();
        self.gw = null;
      },
      // Read per-dispatch; ctx is rebuilt for every incoming message.
      localPlayerId: self.authIdentity,
      emit: (event, payload) => self.emit(event, payload),
    };
    dispatchMessage(ctx, msg);
  }

  private handleClose(source: string): void {
    this.phase = 'idle';
    this.emit('disconnect', { source, playerId: this.currentPlayerId });
  }

  private emit(event: string, payload: unknown): void {
    const listeners = this.listeners.get(event);
    if (!listeners) return;
    for (const fn of listeners) {
      fn(payload as Record<string, unknown>);
    }
  }

  disconnect(): void {
    this.gw?.close();
    this.ms?.close();
    this.gw = null;
    this.ms = null;
    this.phase = 'idle';
    this.listeners.clear();
  }

  setReconnectToken(token: string): void {
    this.reconnectToken = token;
  }

  setReconnectOptions(opts: Partial<ReconnectOptions>): void {
    this.reconnectOptions = { ...this.reconnectOptions, ...opts };
  }

  /** Tell the match-server the determinism hash we hold for `tick` (taken
   *  before applying that tick's frame). Feeds its desync vote. */
  reportHash(tick: number, hash: string): void {
    this.send('ms', { type: 'client:hash', tick, hash });
  }

  async reconnect(lastTick?: number): Promise<boolean> {
    if (!this.serverUrl || !this.reconnectToken || !this.currentPlayerId) return false;

    const opts = this.reconnectOptions;
    const playerId = this.currentPlayerId;
    const reconnectToken = this.reconnectToken;
    const url = this.serverUrl;

    for (let attempt = 0; attempt < opts.maxAttempts; attempt++) {
      if (attempt > 0) {
        const lastIdx = opts.baseDelays.length - 1;
        const base =
          opts.baseDelays[Math.min(attempt - 1, lastIdx)] ?? opts.baseDelays[lastIdx] ?? 1000;
        const jitter = Math.random() * base * 0.25;
        await new Promise((r) => setTimeout(r, base + jitter));
      }

      const ok = await this.attemptReconnect(
        url,
        playerId,
        reconnectToken,
        opts.connectionTimeoutMs,
        lastTick,
      );
      if (ok) {
        this.reconnectAttempts = 0;
        return true;
      }
    }

    return false;
  }

  private attemptReconnect(
    url: string,
    playerId: string,
    reconnectToken: string,
    connectionTimeoutMs: number,
    lastTick?: number,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      let settled = false;
      const settle = (ok: boolean): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(ok);
      };

      const ws = new WebSocket(url);
      this.ms = ws;

      const timer = setTimeout(() => {
        ws.close();
        settle(false);
      }, connectionTimeoutMs);

      ws.addEventListener('open', () => {
        this.phase = 'match';
        this.send('ms', {
          type: 'player:rejoin',
          playerId,
          reconnectToken,
          lastTick,
        });
      });

      ws.addEventListener('message', (e) => {
        const msg = JSON.parse(e.data as string);
        if (msg.type === 'rejoin:ack') {
          this.emit('rejoin', msg);
          settle(true);
        } else if (msg.type === 'error') {
          this.phase = 'idle';
          settle(false);
        } else {
          this.dispatch(msg);
        }
      });

      ws.addEventListener('close', () => this.handleClose('reconnect'));

      ws.addEventListener('error', () => {
        this.phase = 'idle';
        settle(false);
      });
    });
  }
}
